// Стор задач генерации: единственное место, где живёт цикл опроса KIE.
//
// Зачем модуль вообще существует. До него цикл опроса лежал в React-стейте компонента
// (`pollForResult`, скопирован в девять инструментов). taskId не сохранялся нигде, поэтому F5,
// клик по меню или краш вкладки убивали цикл: задача на KIE продолжала считаться, результат
// доходил в никуда, в серверную историю ничего не писалось. Здесь taskId попадает в localStorage
// ДО первого опроса — этого достаточно, чтобы поднять задачу после перезагрузки: воркфлоу
// `/image/poll-kie` без состояния, источник правды — KIE.
//
// Модуль не-React и SSR-безопасный: на верхнем уровне нет ни одного обращения к window или
// localStorage, стор поднимается лениво при первом обращении.

import * as history from './history';
import { checkCreditsBalance, deductCredits, getCurrentUser, getModelPrice, SERVICE_PRICES } from './credits';
import { sendWebhookRequest } from '../config/webhook-urls';
import { n8nRequest, N8N_CONFIG } from '../config/n8n-config';

export const STORAGE_KEY = 'airun-jobs';
export const MAX_JOBS = 200;

const POLL_INTERVAL_MS = 5000;
const MAX_ATTEMPTS = 150;        // ~12.5 минут, как в PhotoGeneration.js
const POLL_TIMEOUT_MS = 30000;   // таймаут одного запроса опроса
const MAX_BACKOFF_MS = 20000;
// Восстанавливать имеет смысл свежую задачу. Сутки — потолок, после которого зависшая в
// 'generating' запись превратилась бы в вечную «1 задача в работе» в индикаторе очереди.
const MAX_RESTORE_AGE_MS = 24 * 60 * 60 * 1000;
// Сколько ждать taskId у задачи в статусе queued, прежде чем считать её мёртвой. Вебхук
// генерации отвечает за один round-trip к KIE, то есть за секунды.
const QUEUED_GRACE_MS = 90000;

// Тексты один в один из PhotoGeneration.js:465-478 и ReplaceModel.js:1365-1395.
const ERROR_TEXT = {
  sensitive: '🚫 Контент был помечен как чувствительный. Попробуйте изменить описание.',
  timeout: '⏰ Превышено время обработки. Повторите позже.',
  network: '🌐 Ошибка сети. Проверьте подключение к интернету и попробуйте еще раз.',
  filesize: '📏 Размер изображения превышает лимиты сервера. Пожалуйста, используйте изображение меньшего размера.'
};

const listeners = new Set();
// Ключи задач с живым циклом опроса. Второй цикл на тот же taskId не стартует: два цикла —
// это два COMPLETED и два похода за списанием.
const polling = new Set();

let jobs = null;       // null = стор ещё не поднят
let snapshot = null;   // кэш стабильной ссылки для useSyncExternalStore

// Задачи, удалённые ЭТОЙ вкладкой. Нужны слиянию: без них чужая копия удалённой задачи
// вернулась бы обратно первым же коммитом соседа.
const tombstones = new Set();

// Постобработка результата до записи в историю (Ретушь вклеивает ответ KIE обратно в исходное
// фото): toolId → (job, rawUrl) => Promise<data:URL|null>. Ключ — инструмент, а не задача:
// функцию в localStorage не положишь, а реестр по toolId переживает F5 и уход со страницы —
// свои входы (для Ретуши — оригинал и маску) инструмент достаёт сам по job.id.
// ponytail: финализатор срабатывает, только если модуль инструмента загружен на странице, где
// задача завершилась. Ретушь регистрируется из AppShell (он в бандле каждой страницы); новый
// инструмент с финализатором надо подключать туда же, иначе в историю ляжет сырой ответ.
const finalizerRegistry = {};

export function registerFinalizer(toolId, fn) {
  finalizerRegistry[toolId] = fn;
}

// В Chrome с заблокированными данными сайта обращение к localStorage бросает SecurityError,
// и typeof от него не спасает — он вычисляет выражение. Без try/catch стор падал бы на импорте.
const isBrowser = () => {
  try {
    return typeof window !== 'undefined' && !!localStorage;
  } catch (e) {
    return false;
  }
};

// --- хранилище ------------------------------------------------------------------------------

// Мусор из localStorage (null, строки, записи без id) сюда не проходит: он доезжал бы до
// рендера и ронял getJobs() внутри useSyncExternalStore — то есть белый экран без выхода,
// потому что при следующей загрузке мусор всё ещё на месте.
function readStore() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((j) => j && typeof j === 'object' && !Array.isArray(j) && j.id);
  } catch (e) {
    return [];
  }
}

// Порог, после которого payload задачи в localStorage не едет. Обычный payload — это ссылки
// на загруженные файлы, единицы килобайт; в 50 КБ не влезает только base64.
const MAX_PERSISTED_PAYLOAD = 50000;

// В памяти payload остаётся целым — на лету он нужен и работает. Обрезается только копия,
// уезжающая в localStorage: у vector-ai «Доработать» кладёт в payload.input_image data-URL
// на сотни килобайт, а квота localStorage ~5MB на всё. Переполнение здесь особенно неприятно
// тем, что молчит: setItem бросает, writeStore возвращает false, и весь тред не переживает F5.
// ponytail: потолок — «Доработать» у vector-ai не переживает перезагрузку (payload не
// восстановится, а без taskId такая задача честно уходит в error «восстанавливать нечего»);
// настоящий фикс — держать источник доработки в IndexedDB и ссылаться на него по id.
function stripHeavyPayload(job) {
  if (!job || !job.payload) return job;
  const serialized = JSON.stringify(job.payload);
  if (!serialized || serialized.length <= MAX_PERSISTED_PAYLOAD) return job;
  return { ...job, payload: null };
}

function writeStore(items) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items.map(stripHeavyPayload)));
    return true;
  } catch (e) {
    return false;
  }
}

// Первыми под нож идут самые старые завершённые; активные задачи не трогаем никогда.
function trim() {
  if (jobs.length <= MAX_JOBS) return;
  const finished = jobs
    .filter((j) => j.status === 'done' || j.status === 'error')
    .sort((a, b) => (a.startedAt || 0) - (b.startedAt || 0));
  let excess = jobs.length - MAX_JOBS;
  const doomed = new Set();
  for (const job of finished) {
    if (excess <= 0) break;
    doomed.add(job.id);
    excess--;
  }
  if (doomed.size > 0) {
    doomed.forEach((id) => tombstones.add(id));
    jobs = jobs.filter((j) => !doomed.has(j.id));
  }
}

// Слияние с тем, что уже лежит в localStorage. Без него вкладка писала весь свой массив
// поверх чужого и стирала задачи соседней вкладки: соседняя задача исчезала из стора, её
// claimDeduction не находил запись — и списание за неё не происходило вовсе (бесплатная
// генерация), а лента теряла результат.
// Правила: чужая запись сохраняется как есть, удалённая этой вкладкой не воскресает, а по
// общим записям выигрывает та, что ПРОДВИНУЛАСЬ ДАЛЬШЕ. Наивное «своя всегда побеждает» было
// ошибкой: копия задачи в соседней вкладке устаревает, и её коммит откатывал уже записанный
// `done` обратно в `generating` — задача завершалась второй раз и списывалась дважды.
// ponytail: между чтением и записью остаётся окно — localStorage не даёт транзакций, и две
// вкладки, коммитящие в одну миллисекунду, всё ещё могут разойтись. Потолок закрыт снаружи:
// списание идемпотентно по task_id на сервере (PR4). Апгрейд, если окно станет заметным, —
// Web Locks API или единственный писатель через BroadcastChannel.
// Жизненный цикл статуса односторонний: queued → generating → done|error. Откат назад означает
// потерю уже достигнутого результата, поэтому при слиянии выигрывает больший ранг.
const statusRank = (status) => (status === 'done' || status === 'error' ? 2 : (status === 'generating' ? 1 : 0));

function pickAdvanced(mine, theirs) {
  if (!theirs) return mine;
  const base = statusRank(theirs.status) > statusRank(mine.status) ? theirs : mine;
  return {
    ...base,
    // Монотонные поля: однажды выставленные, они не сбрасываются ни одной из вкладок.
    // Для deducted это прямо денежное правило — откат флага означал бы повторное списание.
    deducted: mine.deducted === true || theirs.deducted === true,
    taskId: base.taskId || mine.taskId || theirs.taskId || null,
    pollUrl: base.pollUrl || mine.pollUrl || theirs.pollUrl || null
  };
}

function mergeWithStore(mine) {
  const byId = new Map(mine.map((j) => [j.id, j]));
  const merged = [];
  for (const stored of readStore()) {
    if (tombstones.has(stored.id)) continue;
    if (byId.has(stored.id)) {
      merged.push(pickAdvanced(byId.get(stored.id), stored));
      byId.delete(stored.id);
    } else {
      merged.push(stored);
    }
  }
  // Наши записи, которых в сторе ещё нет, — они новее всего, поэтому в начало и в исходном порядке.
  const fresh = mine.filter((j) => byId.has(j.id));
  return fresh.concat(merged);
}

function commit() {
  jobs = mergeWithStore(jobs);
  trim();
  writeStore(jobs);
  snapshot = null;
  listeners.forEach((fn) => {
    try { fn(); } catch (e) {}
  });
}

const findJob = (id) => (jobs || []).find((j) => j.id === id) || null;

function updateJob(id, patch) {
  const i = (jobs || []).findIndex((j) => j.id === id);
  if (i === -1) return null;
  jobs[i] = { ...jobs[i], ...patch };
  commit();
  return jobs[i];
}

const isActive = (jobId) => {
  const job = findJob(jobId);
  return !!job && job.status === 'generating';
};

// Цена уже запущенной задачи — по тем же правилам, что и в checkCreditsBalance:
// цена модели, а если она неизвестна (нет в таблице, у видео нет длительности) — базовая
// цена услуги. Ноль здесь недопустим: он занизил бы резерв.
const jobPrice = (job) => {
  const byModel = getModelPrice(job.model, { ...job.deductOptions, resolution: job.resolution });
  return byModel || SERVICE_PRICES[job.serviceType] || 0;
};

// --- подъём стора и восстановление опроса ----------------------------------------------------

function ensureInit() {
  if (jobs !== null) return;
  if (!isBrowser()) {
    jobs = [];
    return;
  }
  const now = Date.now();
  jobs = readStore().map((job) => {
    if (job.status !== 'queued' && job.status !== 'generating') return job;
    // Задача без taskId — либо прерванная перезагрузкой до ответа вебхука, либо ЧУЖАЯ, чей
    // вебхук прямо сейчас в полёте в соседней вкладке. Различаем по возрасту: вебхук отвечает
    // за секунды, поэтому свежую не трогаем, а старше QUEUED_GRACE_MS — действительно мертва.
    // Кредиты за такую не списаны: списание идёт только после успешного результата.
    if (!job.taskId && !job.pollUrl) {
      if (now - (job.startedAt || 0) < QUEUED_GRACE_MS) return job;
      return { ...job, status: 'error', errorType: 'unknown', restored: true, finishedAt: now,
        errorMessage: '❌ Задача прервана до ответа сервера. Кредиты не списаны.' };
    }
    if (now - (job.startedAt || 0) > MAX_RESTORE_AGE_MS) {
      return { ...job, status: 'error', errorType: 'timeout', errorMessage: ERROR_TEXT.timeout,
        restored: true, finishedAt: now };
    }
    return { ...job, status: 'generating', restored: true };
  });
  writeStore(jobs);
  snapshot = null;
  // Опрос запускаем макрозадачей: ensureInit вызывается в том числе из getJobs(), а getJobs()
  // useSyncExternalStore дёргает во время рендера — асинхронщине там делать нечего.
  const toResume = jobs.filter((j) => j.status === 'generating').map((j) => j.id);
  if (toResume.length > 0) setTimeout(() => toResume.forEach(startPolling), 0);
  // Отсрочка не должна превращаться в вечную «задачу в работе»: если владелец не появился,
  // добиваем её по истечении grace.
  const waiting = jobs.filter((j) => j.status === 'queued' && !j.taskId && !j.pollUrl).map((j) => j.id);
  if (waiting.length > 0) setTimeout(() => sweepStaleQueued(waiting), QUEUED_GRACE_MS);
}

function sweepStaleQueued(ids) {
  if (jobs === null) return;
  commit();   // подтянуть состояние соседних вкладок: владелец мог довести задачу до конца
  for (const id of ids) {
    const job = findJob(id);
    if (job && job.status === 'queued' && !job.taskId && !job.pollUrl) {
      fail(id, 'Задача прервана до ответа сервера. Кредиты не списаны.', 'unknown');
    }
  }
}

// --- ошибки ----------------------------------------------------------------------------------

export function classifyError(rawMessage) {
  const message = String(rawMessage || 'Произошла ошибка при генерации изображения');
  let errorType = 'unknown';
  if (message.includes('flagged as sensitive') || message.includes('E005')) {
    errorType = 'sensitive';
  } else if (message.includes('timeout') || message.includes('время ожидания')) {
    errorType = 'timeout';
  } else if (message.includes('network') || message.includes('fetch')) {
    errorType = 'network';
  } else if (message.includes('size') || message.includes('limit') || message.includes('413') ||
             message.includes('Request Entity Too Large') || message.includes('превышает лимит')) {
    errorType = 'filesize';
  }
  return { errorType, errorMessage: ERROR_TEXT[errorType] || `❌ ${message}` };
}

function fail(jobId, message, forcedType) {
  const classified = forcedType
    ? { errorType: forcedType, errorMessage: ERROR_TEXT[forcedType] || `❌ ${message}` }
    : classifyError(message);
  // finishedAt проставляется на КАЖДОМ переходе в терминальный статус: лента считает по нему
  // длительность («Nano banana 2.1 · 19 с»). У записей, созданных до появления поля, его нет —
  // это валидно, рендер такой случай учитывает сам.
  updateJob(jobId, { status: 'error', finishedAt: Date.now(), ...classified });
}

// --- опрос -----------------------------------------------------------------------------------

// ponytail: ключ цикла — taskId, а при его отсутствии сам poll_url. Две РАЗНЫЕ задачи с
// одинаковым poll_url (легаси-путь `/image/poll` без taskId) поделят один цикл, и вторая
// зависнет в generating до истечения суток. Сегодня все живые пресеты ходят через
// `/image/poll-kie?taskId=…`, поэтому случай недостижим; апгрейд — ключевать циклы по job.id
// и дедуплицировать сетевой запрос отдельно.
const pollKey = (job) => job.taskId || job.pollUrl || null;

// Хвост после taskId обрезаем по &: poll_url вида `...?taskId=abc&x=1` иначе дал бы taskId
// «abc&x=1», и опрос ушёл бы за несуществующей задачей.
const parseTaskId = (pollUrl) => {
  const tail = String(pollUrl || '').split('taskId=')[1];
  return tail ? (tail.split('&')[0] || null) : null;
};

// Все три формы output из оригинала: строка-URL, JSON-строка, массив.
function parseOutput(output) {
  if (!output) return [];
  if (Array.isArray(output)) {
    return output.filter((u) => typeof u === 'string' && u.startsWith('http'));
  }
  if (typeof output !== 'string') return [];
  if (output.startsWith('http')) return [output];
  try {
    const parsed = JSON.parse(output);
    if (Array.isArray(parsed)) {
      return parsed.filter((u) => typeof u === 'string' && u.startsWith('http'));
    }
    if (parsed && Array.isArray(parsed.resultUrls)) return parsed.resultUrls;
  } catch (e) {}
  return [];
}

function failMessage(data) {
  if (data.error_message) {
    return typeof data.error_message === 'object'
      ? (data.error_message.details || data.error_message.error || JSON.stringify(data.error_message))
      : String(data.error_message);
  }
  if (data.details) return String(data.details);
  return 'Workflow завершился с ошибкой';
}

async function pollOnce(job) {
  const pollUrl = job.pollUrl || '';
  const isKiePoll = pollUrl.includes('/image/poll-kie');
  const endpoint = isKiePoll ? '/image/poll-kie' : '/image/poll';
  const data = isKiePoll ? { taskId: job.taskId || parseTaskId(pollUrl) } : { poll_url: pollUrl };
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), POLL_TIMEOUT_MS);
  let response;
  try {
    response = await fetch('/api/n8n-proxy', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ endpoint, data }),
      signal: controller.signal
    });
  } finally {
    clearTimeout(timer);
  }
  const raw = await response.json();
  return (raw && (raw.data || raw)) || {};
}

function startPolling(jobId) {
  const job = findJob(jobId);
  if (!job || job.status !== 'generating') return;
  const key = pollKey(job);
  if (!key || polling.has(key)) return;
  polling.add(key);

  let attempts = 0;
  let consecutiveErrors = 0;
  const stop = () => polling.delete(key);

  const tick = async () => {
    // Задачу могли отменить (cancelJob удаляет запись) или завершить в другой вкладке.
    if (!isActive(jobId)) { stop(); return; }
    attempts++;
    try {
      const data = await pollOnce(findJob(jobId));
      if (!isActive(jobId)) { stop(); return; }
      consecutiveErrors = 0;
      if (data.status === 'COMPLETED') {
        const images = parseOutput(data.output);
        stop();
        if (images.length === 0) {
          fail(jobId, 'Результат обработки не найден в ответе workflow.');
          return;
        }
        await complete(findJob(jobId), images);
        return;
      }
      if (data.status === 'FAILED') {
        stop();
        fail(jobId, failMessage(data));
        return;
      }
      // QUEUED / IN_PROGRESS / PROCESSING / неизвестный — продолжаем опрос
      if (attempts < MAX_ATTEMPTS) {
        setTimeout(tick, POLL_INTERVAL_MS);
        return;
      }
      stop();
      fail(jobId, 'Превышено время ожидания результата.', 'timeout');
    } catch (e) {
      if (attempts < MAX_ATTEMPTS) {
        consecutiveErrors++;
        setTimeout(tick, Math.min(POLL_INTERVAL_MS + consecutiveErrors * 3000, MAX_BACKOFF_MS));
        return;
      }
      stop();
      fail(jobId, 'Ошибка сети.', 'network');
    }
  };

  // Первый опрос сразу: у восстановленной задачи результат, скорее всего, уже готов.
  setTimeout(tick, 0);
}

// --- завершение и списание -------------------------------------------------------------------

// Флаг deducted — ОПТИМИЗАЦИЯ, а не гарантия: она экономит лишний запрос к серверу, когда
// видно, что списание уже прошло. Настоящая защита от двойного списания — ключ идемпотентности
// task_id на стороне n8n (PR4), потому что localStorage не даёт ни блокировки, ни атомарного
// compare-and-set: между чтением и записью соседняя вкладка может успеть что угодно, и никакое
// перечитывание этого не ловит (раньше здесь стояла именно такая декоративная проверка).
//
// Поэтому единственная причина НЕ звать сервер — уже видимый флаг deducted. Во всех остальных
// случаях (записи нет в сторе, запись флага не удалась, стор переполнен) зовём: пропустить
// списание страшнее, чем позвать его второй раз, — второй раз сервер отобьёт по ключу,
// а пропуск означает бесплатную генерацию.
function claimDeduction(jobId) {
  const stored = readStore().find((j) => j.id === jobId);
  if (stored && stored.deducted === true) {
    // Списала другая вкладка. Флаг переносим себе: иначе наш следующий commit() затрёт его
    // в localStorage, и следующее завершение задачи пойдёт списывать снова.
    updateJob(jobId, { deducted: true });
    return false;
  }
  // updateJob → commit() сливается со свежим содержимым localStorage и пишет флаг туда же:
  // это и есть read-modify-write. Результат записи не проверяем осознанно — при переполнении
  // квоты флаг не сохранится, но списание всё равно должно произойти.
  updateJob(jobId, { deducted: true });
  return true;
}

// Пауза перед второй попыткой сохранить блоб результата. Секунды, а не мгновение: типичная
// причина провала — медленный ответ CDN KIE, и повтор в тот же миг упал бы так же.
const RETRY_BLOB_MS = 1800;

// Блоб в IndexedDB — единственная копия картинки, которая переживает и истечение ссылки KIE,
// и задержку токена /api/proxy-image (он начинает работать только после SAVE_GENERATION).
// Поэтому провал — не повод молчать: одна повторная попытка, а если не вышло и она, вызывающий
// пишет blobMissing в запись задачи, и лента знает, что картинку надо доставать самой.
// Возвращает true, если блоб лёг. Не бросает никогда: готовый результат от этого не зависит.
async function saveResultBlob(cfg, id, sourceUrl) {
  for (let attempt = 0; attempt < 2; attempt++) {
    if (attempt > 0) await new Promise((resolve) => setTimeout(resolve, RETRY_BLOB_MS));
    try {
      await history.saveBlob(cfg, id, sourceUrl);
      return true;
    } catch (e) {}
  }
  return false;
}

async function complete(job, images) {
  if (!job) return;
  // Задачу мог довести до конца кто-то ещё (соседняя вкладка, второй цикл после гонки).
  // Тогда история, токены и SAVE_GENERATION уже записаны: повтор дал бы дубль в ленте,
  // второй токен прокси на ту же картинку и вторую строку в серверной истории.
  const known = readStore().find((j) => j.id === job.id);
  if (known && known.status === 'done') {
    updateJob(job.id, {
      status: 'done', resultUrl: known.resultUrl || null,
      // Ссылку KIE и признак отсутствующего блоба забираем у победителя: восстановление
      // картинки в ленте опирается на них, а у нашей копии записи их нет.
      sourceUrl: known.sourceUrl || images[0] || null,
      blobMissing: known.blobMissing === true,
      finalized: known.finalized,
      resultInHistory: known.resultInHistory === true,
      finishedAt: known.finishedAt || Date.now(),
      deducted: known.deducted === true,
      errorType: null, errorMessage: null
    });
    return;
  }
  const tokens = images.map(() => `img_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const publicUrls = tokens.map((t) => `/api/proxy-image?token=${t}`);

  // Финализатор меняет только то, что ляжет блобом в историю. Токены прокси, SAVE_GENERATION,
  // списание и sourceUrl остаются на сырой ссылке KIE. Сбой склейки — не повод терять
  // оплаченный результат: сохраняем сырой.
  let blobSrc = images[0];
  const fin = finalizerRegistry[job.toolId];
  if (fin) {
    try {
      blobSrc = (await fin(job, images[0])) || images[0];
    } catch (e) {
      console.warn('jobs: finalize упал, сохраняем сырой результат', e);
    }
  }
  // Пока шла склейка, задачу могли отменить: тогда ни истории, ни списания.
  if (!findJob(job.id)) return;

  const cfg = history.getHistoryConfig(job.toolId);
  let blobMissing = false;
  if (cfg) {
    blobMissing = !(await saveResultBlob(cfg, job.id, blobSrc));
    try {
      history.append(cfg, {
        id: job.id,
        urls: publicUrls,
        prompt: job.prompt,
        // В реальном профиле у большинства записей model отсутствует — здесь тоже допустим null.
        model: job.model || null,
        timestamp: new Date().toLocaleString('ru-RU')
      });
    } catch (e) {}
  }

  try {
    const user = getCurrentUser && getCurrentUser();
    if (user && user.id) {
      await Promise.all(images.map((srcUrl, i) => n8nRequest(N8N_CONFIG.ENDPOINTS.SAVE_GENERATION, {
        user_id: user.id,
        prompt: job.prompt,
        model: job.serviceType,
        kie_model: job.model,
        image_token: tokens[i],
        source_url: srcUrl,
        public_url: publicUrls[i]
      })));
    }
  } catch (e) {}

  if (claimDeduction(job.id)) {
    // Сбой списания не отменяет готовый результат — так ведут себя все девять инструментов.
    try {
      await deductCredits(job.serviceType, false, job.model, { ...job.deductOptions, resolution: job.resolution }, job.taskId || job.id);
    } catch (e) {}
  }

  // sourceUrl — короткая строка, квоту localStorage не бьёт, а без неё восстановление картинки
  // в ленте может опираться только на токен прокси, который в момент сбоя ещё не работает.
  updateJob(job.id, {
    status: 'done', resultUrl: publicUrls[0], sourceUrl: images[0] || null, blobMissing,
    // Для инструментов с финализатором: false — склейка не удалась, в блобе сырой ответ.
    finalized: fin ? blobSrc !== images[0] : undefined,
    finishedAt: Date.now(), errorType: null, errorMessage: null
  });
}

// Завершение синхронного движка (vector-ai): вебхук держал HTTP-запрос всю генерацию
// (10 с — 3 мин) и вернул картинку data-URL'ом. Опроса, taskId и серверной истории здесь нет —
// SAVE_GENERATION у векторов не было никогда, история только локальная.
async function completeSync(jobId, dataUrl) {
  const job = findJob(jobId);
  if (!job) return;

  const cfg = history.getHistoryConfig(job.toolId);
  if (cfg) {
    // Сначала блоб, потом запись истории — иначе загрузчик блобов увидит запись раньше
    // сохранения и пометит её недоступной (VectorAI.js:200).
    try { await history.saveBlob(cfg, job.id, dataUrl); } catch (e) {}
    try {
      // Форма записи vector-ai: одиночный url и НИКАКОГО urls[] — так исторически.
      const item = {
        id: job.id,
        prompt: job.prompt,
        model: job.model || null,
        timestamp: new Date().toLocaleString('ru-RU')
      };
      // Крупный SVG в localStorage не кладём (квота ~5MB) — блоб уже лежит в IndexedDB.
      if (dataUrl.length <= history.MAX_INLINE_URL_LENGTH) item.url = dataUrl;
      history.append(cfg, item);
    } catch (e) {}
  }

  // taskId у синхронного движка нет: списание уходит без task_id, легаси-путём, как сегодня.
  if (claimDeduction(job.id)) {
    try {
      await deductCredits(job.serviceType, false, job.model, { ...job.deductOptions, resolution: job.resolution }, job.taskId || job.id);
    } catch (e) {}
  }

  // resultUrl остаётся null умышленно: data-URL на сотни килобайт в `airun-jobs` пробил бы
  // квоту localStorage и снёс бы весь стор. Лента берёт картинку из IndexedDB —
  // history.loadBlobUrl(cfg, job.id).
  updateJob(job.id, {
    status: 'done', resultUrl: null, resultInHistory: true, finishedAt: Date.now(),
    errorType: null, errorMessage: null
  });
}

// --- публичный API ---------------------------------------------------------------------------

// webhook, payload и serviceType приходят уже собранными: стор ничего не знает о пресетах.
// Бросает (реджектит) при нехватке кредитов — до отправки вебхука. Возвращает id задачи, не
// дожидаясь ни ответа вебхука, ни тем более результата генерации.
export async function startJob(opts) {
  const o = opts || {};
  ensureInit();
  // Разрешение нормализуем один раз: проверка баланса и списание обязаны считать одну и ту же
  // цену. Без разрешения getModelPrice молча срывается на базовую цену услуги — проверили бы
  // одно, списали другое. У всех моделей в MODEL_PRICES есть 1K, он и дефолт.
  const resolution = o.resolution || '1K';
  // Необязательные доп-поля цены, о которых стор ничего не знает: у видео цена зависит ещё от
  // длительности и наличия аудио (`{ duration, generate_audio }`), и сервер пересчитывает её по
  // ним же. Поля идут и в проверку баланса, и в списание — иначе проверили бы одну цену, а
  // списали другую. Хранятся в самой записи задачи: списание происходит после опроса, то есть
  // возможно уже после перезагрузки страницы, когда параметров формы больше нет.
  const deductOptions = o.deductOptions || null;
  // Бросает при нехватке — пробрасываем наружу, вызывающий показывает сообщение.
  const check = checkCreditsBalance(o.serviceType, false, o.model, { ...deductOptions, resolution });

  // Резерв под уже запущенные задачи. Списание происходит на финише, а баланс проверялся на
  // старте — при балансе на одну генерацию десять задач подряд проходили проверку все десять,
  // уходили на KIE, и списывалась ровно одна: остальные девять получали ошибку списания,
  // которая штатно глотается, то есть оказывались бесплатными.
  const reserved = jobs
    .filter((j) => j.status === 'queued' || j.status === 'generating')
    .reduce((sum, j) => sum + jobPrice(j), 0);
  if (check.currentCredits < reserved + check.price) {
    throw new Error(`Недостаточно кредитов. У вас: ${check.currentCredits}, требуется: ${reserved + check.price}`);
  }

  const id = `job_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  jobs.unshift({
    id,
    toolId: o.toolId,
    serviceType: o.serviceType,
    webhook: o.webhook,
    payload: o.payload,
    taskId: null,
    pollUrl: null,
    prompt: o.prompt || '',
    attachments: Array.isArray(o.attachments) ? o.attachments : [],
    model: o.model || null,
    aspectRatio: o.aspectRatio || null,
    resolution,
    deductOptions,
    status: 'queued',
    startedAt: Date.now(),
    finishedAt: null,
    resultUrl: null,
    // Исходная ссылка KIE рядом с токеном прокси и признак «блоб сохранить не удалось»:
    // по ним лента дозаписывает картинку в IndexedDB, если на финише это не получилось.
    sourceUrl: null,
    blobMissing: false,
    // Синхронный движок (vector-ai) отдаёт картинку data-URL'ом: в стор она не попадает,
    // результат лежит только в IndexedDB. Флаг говорит ленте, откуда его брать.
    resultInHistory: false,
    errorType: null,
    errorMessage: null,
    restored: false,
    deducted: false
  });
  commit();

  // Дальше — фоном: вызывающий уже получил id.
  (async () => {
    try {
      // Ровно { retries: 0 }: повтор на 504 запускает ДУБЛИРУЮЩУЮ генерацию за деньги.
      const data = (await sendWebhookRequest(o.webhook, o.payload, { retries: 0 })) || {};
      // Асинхронный движок (KIE): вернулся poll_url, дальше опрос.
      if (data.poll_url) {
        // taskId ложится в localStorage ДО первого опроса — это и есть фикс перезагрузки.
        updateJob(id, { taskId: data.taskId || parseTaskId(data.poll_url), pollUrl: data.poll_url });
        updateJob(id, { status: 'generating' });
        startPolling(id);
        return;
      }
      // Синхронный движок (vector-ai): запрос висел всю генерацию и вернул картинку сразу.
      // Условие один в один из VectorAI.js:318 — success:false отменяет результат.
      if (data.success !== false && typeof data.image === 'string' && data.image.startsWith('data:image/')) {
        await completeSync(id, data.image);
        return;
      }
      fail(id, data.error || 'Не получен результат от workflow API');
    } catch (e) {
      fail(id, e && e.message);
    }
  })();

  return id;
}

// Снимает задачу из локальной очереди. Генерация на KIE при этом ДОИГРЫВАЕТ до конца —
// серверной отмены нет; кредиты за отменённую задачу не списываются (списание идёт только
// после успешного опроса, а опрашивать уже некому).
export function cancelJob(id) {
  ensureInit();
  const job = findJob(id);
  if (!job) return false;
  const key = pollKey(job);
  if (key) polling.delete(key);
  // Без метки об удалении слияние вернуло бы задачу из чужой копии стора обратно в ленту.
  tombstones.add(id);
  jobs = jobs.filter((j) => j.id !== id);
  commit();
  return true;
}

// Новая задача с теми же параметрами; overrides — { model?, aspectRatio?, resolution? } поверх
// них («повторить с другой моделью/размером»). Возвращает Promise с новым id (или null, если
// исходной нет).
export function retryJob(id, overrides) {
  ensureInit();
  const job = findJob(id);
  if (!job) return null;
  const o = { model: job.model, aspectRatio: job.aspectRatio, resolution: job.resolution, ...overrides };
  return startJob({
    toolId: job.toolId,
    serviceType: job.serviceType,
    webhook: job.webhook,
    payload: patchPayload(job.payload, o),
    prompt: job.prompt,
    attachments: job.attachments,
    model: o.model,
    aspectRatio: o.aspectRatio,
    resolution: o.resolution,
    deductOptions: job.deductOptions
  });
}

// Те же настройки — в payload, который уходит на бэкенд. buildPayload пресета отсюда не виден
// (и его входов в задаче нет), а имена полей у воркфлоу разные: kie_model у фото-движков,
// model у вектора (presets.js). Правим по наличию ключа.
// ponytail: пресет с третьим именем поля молча уедет со старой моделью — тогда хранить в
// задаче входы buildPayload и пересобирать через пресет.
function patchPayload(payload, o) {
  if (!payload || typeof payload !== 'object') return payload;
  const p = { ...payload };
  if ('kie_model' in p) p.kie_model = o.model;
  else if ('model' in p) p.model = o.model;
  if ('aspect_ratio' in p) p.aspect_ratio = o.aspectRatio;
  if ('resolution' in p) p.resolution = o.resolution;
  return p;
}

// Ссылка стабильна, пока стор не менялся — иначе useSyncExternalStore зациклится.
export function getJobs() {
  ensureInit();
  if (!snapshot) snapshot = jobs.slice();
  return snapshot;
}

export function subscribe(fn) {
  ensureInit();
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// Импорт модуля (AppShell) поднимает стор в браузере и возобновляет опрос, чтобы задачи жили
// между переходами. typeof-проверка — не обращение к window, при SSR ветка не выполняется;
// сама работа отложена в макрозадачу, чтобы во время импорта ничего не происходило.
if (typeof window !== 'undefined') {
  setTimeout(() => { try { ensureInit(); } catch (e) {} }, 0);
}
